/* Inside the PathsAbroad phone app (mobile/), links to other sites open in the app's own browser instead of leaving the
   app, and application portals there get the "Fill with PathsAbroad" button. The site (apps/web and the planner's
   bridge) installs this; outside the app it does nothing. The first time a link is opened, it loads the app's bridge
   (/app/bridge.js, built from apps/extension/src/app/bridge.ts), which opens the page. */

type AppBridge = { openPortal(url: string, opts?: { lang?: string }): Promise<boolean> };
type Cap = { isNativePlatform?(): boolean };

/* true inside the phone app (the app gives every page window.Capacitor) */
export function isNativeApp(win: unknown = globalThis): boolean {
  try { return !!(win as { Capacitor?: Cap }).Capacitor?.isNativePlatform?.(); } catch { return false; }
}

let loading: Promise<AppBridge | null> | null = null;
export const APP_BRIDGE_SRC = '/app/bridge.js';

/* the app's bridge, loaded once (same origin, so the site's CSP allows it) */
export function loadAppBridge(doc: Document = document): Promise<AppBridge | null> {
  const have = () => (window as unknown as { PathsAbroadApp?: AppBridge }).PathsAbroadApp ?? null;
  if (have()) return Promise.resolve(have());
  loading ??= new Promise(resolve => {
    const s = doc.createElement('script');
    s.src = APP_BRIDGE_SRC;
    s.async = true;
    s.onload = () => resolve(have());
    s.onerror = () => { loading = null; resolve(null); };
    doc.head.appendChild(s);
  });
  return loading;
}

/* Send taps on links to other sites (https only) to the app's own browser. `lang` is the site's language at the time. */
export function installAppLinks(opts: { lang: () => string; doc?: Document; win?: Window } = { lang: () => 'en' }): boolean {
  const doc = opts.doc ?? document, win = opts.win ?? window;
  if (!isNativeApp(win)) return false;
  doc.addEventListener('click', e => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a || a.hasAttribute('download')) return;
    let u: URL;
    try { u = new URL(a.href, win.location.href); } catch { return; }
    if (u.protocol !== 'https:' || u.origin === win.location.origin) return;
    e.preventDefault();
    void loadAppBridge(doc).then(b => (b ? b.openPortal(u.href, { lang: opts.lang() }) : false)).then(opened => {
      if (!opened) win.open(u.href, '_blank', 'noopener');   // the system browser, as before
    });
  }, true);
  return true;
}
